
import MIcon from "@/components/MIcon.vue";
import MTableCell from "@/components/MTableCell.vue";
import { TableDataType, TableDefinition, formatUnixtime } from "@/utils/table";
import { Options, Vue } from "vue-class-component";

function formatValue(value: string | number | boolean, type: TableDataType) {
  if (type === "unixtime") {
    return formatUnixtime(value as number);
  } else {
    return value?.toString();
  }
}

function generateCellData(
  data: { [key: string]: string | number | boolean },
  definition: TableDefinition
): CellData {
  let text = "";
  let shouldDisableButton = false;
  if (definition.type === "button") {
    text = definition.text;

    if (
      definition.button?.disableKeys &&
      Object.keys(definition.button.disableKeys).length > 0
    ) {
      const { keys, operator } = definition.button.disableKeys;
      const bools = Object.entries(keys).map(([k, v]) => data[k] == v);

      shouldDisableButton =
        operator === "and"
          ? bools.every(bool => bool === true)
          : bools.some(bool => bool === true);
    }
  } else if (definition.key === "recessTime") {
    if (data[definition.key] && data[definition.key] > 0) {
      text = "休止";
    } else {
      text = "有効";
    }
  } else if (definition.subType === "original") {
    text = data[definition.key] ? data[definition.key].toString() : "";
  } else if (definition.key === "schoolAILinkageStatus") {
    text = data[definition.key] ? data[definition.key].toString() : "";
    // if (data[definition.key] === true) {
    //   text = "連携済み";
    // } else {
    //   text = "未連携";
    // }
  } else if (definition.key === "qandaLinkageStatus") {
    text = data[definition.key] ? data[definition.key].toString() : "";
    // if (data[definition.key] === true) {
    //   text = "連携済み";
    // } else {
    //   text = "未連携";
    // }
  } else {
    text = formatValue(data[definition.key].toString(), definition.type);
  }

  return {
    // text:
    //   definition.key === "recessTime"
    //     ? data[definition.key] && data[definition.key] > 0
    //       ? "休止"
    //       : "有効"
    //     : definition.subType === "original"
    //     ? data[definition.key]
    //       ? data[definition.key].toString()
    //       : ""
    //     : formatValue(data[definition.key].toString(), definition.type),
    text: text,
    prefix: definition.prefix
      ? formatValue(data[`pre-${definition.key}`], definition.prefixType)
      : "",
    suffix: definition.suffix
      ? formatValue(data[`suf-${definition.key}`], definition.suffixType)
      : "",
    size: definition.size,
    maxSize: definition.maxSize,
    showOnMobile: definition.showOnMobile,
    extraClass: definition.extraClass,
    property: definition.key,
    hover: definition.hover,
    copyable: definition.copyable ?? false,
    isPinnedCol: definition.isPinnedCol ?? false,
    isButton: definition.type === "button",
    button: definition.button
      ? {
          color: definition.button.color ?? "",
          disable: shouldDisableButton,
          text: definition.button.text ?? "",
          buttonKey: definition.button.buttonKey ?? ""
        }
      : null
  };
}

function generateRowData(
  data: { [key: string]: string | number | boolean },
  definitions: TableDefinition[],
  selectedIds: string[],
  coloredRowList: {
    key: string;
    data: string | number | boolean;
    color: string;
  }[]
): RowData {
  let id: string;
  if (Object.keys(data).includes("id")) {
    id = data.id as string;
  } else {
    id = "";
  }

  let schoolDocId: string;
  if (Object.keys(data).includes("schoolDocId")) {
    schoolDocId = data.schoolDocId as string;
  } else {
    schoolDocId = "";
  }

  let classroomDocId: string;
  if (Object.keys(data).includes("classroomDocId")) {
    classroomDocId = data.classroomDocId as string;
  } else {
    classroomDocId = "";
  }

  let link: string;
  if (Object.keys(data).includes("link")) {
    link = data.link as string;
  } else {
    link = "";
  }

  let order: number;
  if (Object.keys(data).includes("order")) {
    order = data.order as number;
  } else {
    order = 0;
  }

  let relatedLink: string;
  if (Object.keys(data).includes("relatedLink")) {
    relatedLink = data.relatedLink as string;
  } else {
    relatedLink = "";
  }

  if (
    Object.keys(data).includes("properties") &&
    Array.isArray(data.properties)
  ) {
    data.properties.map(item => {
      const { id, value } = item;
      data[id] = value;
    });
  }

  const datas: CellData[] = definitions.map(def => generateCellData(data, def));
  let pinnedDatas: CellData[] = [];
  let scrollableDatas: CellData[] = [];

  if (datas.length) {
    const targetIndex = datas.findIndex(d => d.isPinnedCol);
    if (targetIndex < 0) {
      // ピン止めの指定が無し
      scrollableDatas = [...datas];
    } else {
      pinnedDatas = [...datas].slice(0, targetIndex + 1);
      scrollableDatas = [...datas].slice(targetIndex + 1);
    }
  }

  const res = {
    id,
    schoolDocId,
    classroomDocId,
    schoolAIUserId: data.schoolAIUserId as string,
    link,
    order,
    relatedLink,
    select: selectedIds.includes(data.id as string),
    datas,
    pinnedDatas,
    scrollableDatas
  };

  const color = coloredRowList.find(e => e.data === data[e.key])?.color;
  return color ? { ...res, color } : res;
}

type CellData = {
  text: string;
  prefix: string;
  suffix: string;
  size: number;
  maxSize?: string;
  showOnMobile: boolean;
  extraClass?: string;
  property?: string;
  hover?: boolean;
  copyable?: boolean;
  isPinnedCol?: boolean;
  isButton?: boolean;
  button?: {
    color?: string;
    disable?: boolean;
    text?: string;
    buttonKey?: string;
  } | null;
};

type RowData = {
  id: string;
  schoolDocId: string;
  classroomDocId: string;
  link: string;
  order: number;
  select: boolean;
  datas: CellData[];
  pinnedDatas: CellData[];
  scrollableDatas: CellData[];
  color?: string;
  schoolAIUserId?: string;
};

@Options({
  components: {
    MIcon,
    MTableCell
  },
  emits: ["change", "select", "copy", "onCustomButtonClick"],
  props: {
    datas: Array,
    definitions: Array,
    selectedIds: Array,
    order: Boolean,
    selectable: Boolean,
    editable: Boolean,
    useActions: Boolean,
    useSubmission: Boolean,
    coloredRowList: Array,
    disableSelection: Boolean
  },
  watch: {
    datas() {
      this.onUpdateDatas();
    },
    definitions() {
      this.onUpdateDatas();
    }
  }
})
export default class MTableContents extends Vue {
  datas: { [key: string]: string | number | boolean }[] = [];
  definitions: TableDefinition[] = [];
  selectedIds: string[] = [];
  order = false;
  selectable = false;
  editable = false;
  useActions = false;
  useSubmission = false;
  coloredRowList: {
    key: string;
    data: string | number | boolean;
    color: string;
  }[] = [];
  disableSelection = false;

  rowDatas: RowData[] = [];

  async onUpdateDatas() {
    const baseDatas = [...this.datas];
    this.rowDatas = baseDatas.map(data =>
      generateRowData(
        data,
        this.definitions,
        this.selectedIds,
        this.coloredRowList
      )
    );
  }

  created() {
    this.onUpdateDatas();
  }

  change(id: string) {
    this.$emit("change", id);
  }

  select(id: string) {
    this.$emit("select", id);
  }
  copy(id: string) {
    this.$emit("copy", id);
  }

  onCustomButtonClick(id: string, { buttonKey }: { buttonKey: string }) {
    this.$emit("onCustomButtonClick", { id, buttonKey });
  }

  getTargetElement(event: MouseEvent) {
    if (!(event instanceof MouseEvent)) return null;
    const self = event.currentTarget as HTMLElement | null;
    if (!self) return null;
    const stickyContainer = self.firstElementChild;
    return stickyContainer ? stickyContainer : null;
  }

  handleMouseEnter(event: MouseEvent) {
    const target = this.getTargetElement(event);
    if (!target) return;
    target.classList.add("hovered");
  }

  handleMouseLeave(event: MouseEvent) {
    const target = this.getTargetElement(event);
    if (!target) return;
    target.classList.remove("hovered");
  }
}
